import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { SignalLogo } from '../components/SignalLogo';
import { cn } from '../lib/utils';

const labelClassName =
  'block mb-1.5 font-medium text-sm leading-5 text-brand-text';

const baseInputClassName =
  'box-border w-full rounded-[10px] border bg-brand-surface px-3.5 py-3 text-[15px] leading-6 text-brand-text outline-none transition-[border-color,box-shadow,background-color] focus:border-accent-blue focus:bg-white focus:ring-4 focus:ring-accent-blue/10';

const MIN_PASSWORD_LENGTH = 6;

function isEmailTakenError(message: string | null): boolean {
  if (!message) return false;
  const normalized = message.toLowerCase();
  return (
    normalized.includes('already registered') ||
    normalized.includes('already in use') ||
    normalized.includes('already exists') ||
    normalized.includes('user already') ||
    normalized.includes('email address is already')
  );
}

export function SignupPage() {
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation('common');
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const emailMissing = submitted && email.trim().length === 0;
  const passwordMissing = submitted && password.length === 0;
  const confirmMissing = submitted && confirmPassword.length === 0;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setEmailError(null);
    setPasswordError(null);
    setConfirmError(null);
    setSubmitError(null);

    if (!email.trim() || !password || !confirmPassword) {
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(
        t('signup.error.passwordTooShort', { min: MIN_PASSWORD_LENGTH }),
      );
      return;
    }

    if (password !== confirmPassword) {
      setConfirmError(t('signup.error.passwordsDontMatch'));
      return;
    }

    setSubmitting(true);
    const { error: err } = await signUp(email, password);
    setSubmitting(false);

    if (err) {
      if (isEmailTakenError(err)) {
        setEmailError(t('signup.error.emailTaken'));
      } else {
        setSubmitError(err);
      }
      return;
    }

    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col bg-brand-canvas px-4 py-6 sm:px-6">
      <div className="flex justify-end">
        <LanguageSwitcher />
      </div>

      <div className="flex flex-1 items-center justify-center py-8">
        <div className="w-full max-w-[400px]">
          <Link
            to="/"
            className="mb-7 flex items-center justify-center gap-2.5 text-2xl font-semibold leading-8 tracking-[-0.02em] text-brand-text no-underline"
          >
            <SignalLogo size={30} />
            {tc('signal')}
          </Link>

          <div className="rounded-[20px] border border-brand-border bg-white p-8 shadow-[0_8px_32px_rgba(0,0,0,0.06)] sm:p-9">
            <h1 className="mb-6 text-center text-[22px] font-semibold leading-7 tracking-[-0.015em] text-brand-text">
              {t('signup.title')}
            </h1>

            <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-[18px]">
              <div>
                <label htmlFor="email" className={labelClassName}>
                  {t('signup.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  aria-invalid={Boolean(emailError) || emailMissing}
                  className={cn(
                    baseInputClassName,
                    emailError || emailMissing
                      ? 'border-brand-error focus:border-brand-error focus:ring-brand-error/10'
                      : 'border-brand-border-medium',
                  )}
                />
                {emailError && (
                  <p className="mt-1.5 text-[13px] leading-5 text-brand-error">{emailError}</p>
                )}
                {!emailError && emailMissing && (
                  <p className="mt-1.5 text-[13px] leading-5 text-brand-error">
                    {t('signup.error.required')}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="password" className={labelClassName}>
                  {t('signup.password')}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    minLength={MIN_PASSWORD_LENGTH}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (passwordError) setPasswordError(null);
                    }}
                    aria-invalid={Boolean(passwordError) || passwordMissing}
                    className={cn(
                      baseInputClassName,
                      'pr-11',
                      passwordError || passwordMissing
                        ? 'border-brand-error focus:border-brand-error focus:ring-brand-error/10'
                        : 'border-brand-border-medium',
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={
                      showPassword ? t('signup.hidePassword') : t('signup.showPassword')
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer rounded-md border-0 bg-transparent p-1 text-brand-muted hover:text-brand-text"
                  >
                    {showPassword ? (
                      <EyeOff size={18} strokeWidth={1.75} />
                    ) : (
                      <Eye size={18} strokeWidth={1.75} />
                    )}
                  </button>
                </div>
                {passwordError && (
                  <p className="mt-1.5 text-[13px] leading-5 text-brand-error">{passwordError}</p>
                )}
                {!passwordError && passwordMissing && (
                  <p className="mt-1.5 text-[13px] leading-5 text-brand-error">
                    {t('signup.error.required')}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className={labelClassName}>
                  {t('signup.confirmPassword')}
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (confirmError) setConfirmError(null);
                    }}
                    aria-invalid={Boolean(confirmError) || confirmMissing}
                    className={cn(
                      baseInputClassName,
                      'pr-11',
                      confirmError || confirmMissing
                        ? 'border-brand-error focus:border-brand-error focus:ring-brand-error/10'
                        : 'border-brand-border-medium',
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label={
                      showConfirmPassword
                        ? t('signup.hidePassword')
                        : t('signup.showPassword')
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer rounded-md border-0 bg-transparent p-1 text-brand-muted hover:text-brand-text"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={18} strokeWidth={1.75} />
                    ) : (
                      <Eye size={18} strokeWidth={1.75} />
                    )}
                  </button>
                </div>
                {confirmError && (
                  <p className="mt-1.5 text-[13px] leading-5 text-brand-error">{confirmError}</p>
                )}
                {!confirmError && confirmMissing && (
                  <p className="mt-1.5 text-[13px] leading-5 text-brand-error">
                    {t('signup.error.required')}
                  </p>
                )}
              </div>

              {submitError && (
                <p className="text-sm leading-5 text-brand-error" role="alert">
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className={cn(
                  'mt-1 h-[50px] w-full cursor-pointer rounded-full border-0 text-[15px] font-medium leading-6 text-white',
                  submitting
                    ? 'cursor-default bg-[#B0B0B5]'
                    : 'bg-accent-blue hover:bg-accent-blue-hover',
                )}
              >
                {submitting ? t('signup.submitting') : t('signup.submit')}
              </button>
            </form>

            <p className="mt-[22px] text-center text-sm leading-5 text-brand-muted">
              {t('signup.hasAccount')}{' '}
              <Link
                to="/login"
                className="font-medium text-brand-text no-underline hover:text-accent-blue"
              >
                {t('signup.loginLink')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
